'use client';

import { c as _c } from "react/compiler-runtime";
import classnames from 'classnames';
import { useLocale } from 'next-intl';
import { LearnContentCarouselCardImage } from '@module/mdrt-org/shared/block-components/learn-carousel-block/components/learn-content-carousel/components/learn-content-carousel-card-image-with-number/learn-content-carousel-card-image';
import { useLockedResourcePrompt } from '@module/mdrt-org/shared/block-components/learn-carousel-block/components/learn-content-carousel/components/locked-resource-prompt/locked-resource-prompt';
import { ResourceTypeIcon } from '@module/mdrt-org/shared/components/resource-type-icon/resource-type-icon';
import { getExternalUrlOrRelativePath } from '@module/mdrt-org/shared/utils/helpers/get-external-url-or-relative-path';
import { isShortcutExternal } from '@module/mdrt-org/shared/utils/helpers/is-shortcut-external';
import { Badge, BadgeVariant } from '@shared/ui-components/badge/badge';
import { RenderHTML } from '@shared/ui-components/render-html/render-html';
import LockIcon from '@shared/ui-components/svgs/lock-icon';
import { GTM_ACTION_CLICK, GTM_EVENT_NAME_INTERNAL_LINK_CLICK, GTM_EVENT_NAME_OUTBOUND, GTM_EVENT_USER_INTERACTIONS } from '@shared/utils/constants/gtm';
import { useFormatTranslationString } from '@shared/utils/helpers/format-translation-string';
import { getTextFromHtmlString } from '@shared/utils/helpers/get-text-from-html-string';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { CardLink } from '../../../../../components/card-link/card-link';
import { useUserContext } from '../../../../../components/providers/user-context';
import { convertSecondsToMinutes } from '../../../../../utils/helpers/convert-seconds-to-minutes';
import { getCardLinkDescription } from '../../../../../utils/helpers/get-card-link-description';
import { getContentFallbackImageSource } from '../../../../../utils/helpers/get-content-fallback-image-source';
import { getFeaturedPeopleOrAuthors } from '../../../../../utils/helpers/get-featured-people-or-authors';
import { getRelativePathWithoutLocale } from '../../../../../utils/helpers/get-relative-path-without-locale';
import { type LearnCarouselCardData } from '../../../actions/types';
import { type LearnGenericTextsCmsContent } from '../../../graphql/get-learn-generic-texts-cms-content';
import styles from './learn-content-carousel-card.module.scss';
export type LearnContentCarouselCardProps = {
  card: LearnCarouselCardData;
  carouselTitle: string;
  cmsContent: LearnGenericTextsCmsContent;
  selectedFilterValue?: null | string;
  svgNumber?: number | undefined;
};
export const LearnContentCarouselCard = t0 => {
  const $ = _c(37);
  const {
    card,
    carouselTitle,
    cmsContent,
    selectedFilterValue,
    svgNumber
  } = t0;
  const formatTranslationString = useFormatTranslationString();
  const isLocked = card.restrictedMembersContent;
  const t1 = card?.RelativePath;
  let t2;
  if ($[0] !== cmsContent || $[1] !== isLocked || $[2] !== t1) {
    t2 = {
      cmsContent,
      isResourceLocked: isLocked,
      resourceUrl: t1
    };
    $[0] = cmsContent;
    $[1] = isLocked;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    onResourceClick,
    renderLockedResourcePrompt,
    shouldLockedResourcePromptBeShown
  } = useLockedResourcePrompt(t2);
  const {
    isAuthenticated
  } = useUserContext();
  const url = card.ContentLink?.Url;
  const isExternal = isShortcutExternal(card.Shortcut, url);
  const t3 = card?.ContentTypeSelection;
  let t4;
  if ($[4] !== t3) {
    t4 = getContentFallbackImageSource(t3);
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const fallbackImageSource = t4;
  const imageAlt = card?.ThumbnailImage?.AltText ?? card?.Heading ?? "";
  const people = getFeaturedPeopleOrAuthors(card);
  const firstAuthor = people[0];
  const otherAuthorsCount = firstAuthor && people.length > 1 ? people.length - 1 : undefined;
  const description = getCardLinkDescription(card?.SubheadingText, card?.Subheading);
  const locale = useLocale();
  const formattedUrl = getExternalUrlOrRelativePath({
    isExternal,
    relativePath: card.RelativePath ? getRelativePathWithoutLocale(card.RelativePath, locale) : undefined,
    url
  });
  const cardLinkUrl = shouldLockedResourcePromptBeShown ? undefined : formattedUrl;
  const linkProps = cardLinkUrl ? {
    scroll: true,
    target: isExternal ? "_blank" : undefined,
    to: cardLinkUrl
  } : {};
  let t5;
  if ($[6] !== card.ReadTimeInSeconds || $[7] !== cmsContent.minutesLabel || $[8] !== formatTranslationString) {
    t5 = card.ReadTimeInSeconds && card.ReadTimeInSeconds > 0 ? formatTranslationString(cmsContent.minutesLabel, {
      readTime: convertSecondsToMinutes(card.ReadTimeInSeconds)
    }) : null;
    $[6] = card.ReadTimeInSeconds;
    $[7] = cmsContent.minutesLabel;
    $[8] = formatTranslationString;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const formattedReadTimeLabel = t5;
  const sendLockedResourceIntoGTM = () => {
    const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || "";
    const allAuthors = people?.join(", ") || "";
    const labelText = card?.Heading + " " + allAuthors + " " + formattedReadTimeLabel + " " + getTextFromHtmlString(description);
    let label;
    if (isLocked && !card.isRandom) {
      label = "locked_resource_click";
    } else {
      if (!isLocked && card.isRandom) {
        label = "random";
      } else {
        if (isLocked && card.isRandom) {
          label = "locked_resource_click_random";
        }
      }
    }
    sendGtmEvent({
      action: GTM_ACTION_CLICK,
      carousel_header: carouselTitle,
      event: GTM_EVENT_USER_INTERACTIONS,
      eventName: GTM_EVENT_NAME_INTERNAL_LINK_CLICK,
      label,
      link_label: labelText,
      link_url: baseUrl + formattedUrl
    });
  };
  const sendOnDemandCourseIntoGTM = () => {
    sendGtmEvent({
      action: GTM_ACTION_CLICK,
      carousel_header: "On-Demand Courses",
      event: GTM_EVENT_USER_INTERACTIONS,
      eventName: GTM_EVENT_NAME_INTERNAL_LINK_CLICK,
      label: card?.Heading || "",
      link_url: formattedUrl
    });
    if (!new URL(formattedUrl).hostname.endsWith("mdrt.org")) {
      sendGtmEvent({
        action: GTM_ACTION_CLICK,
        carousel_header: "On-Demand Courses",
        event: GTM_EVENT_USER_INTERACTIONS,
        eventName: GTM_EVENT_NAME_OUTBOUND,
        label: card?.Heading || "",
        link_url: formattedUrl
      });
    }
  };
  let t6;
  if ($[10] !== renderLockedResourcePrompt) {
    t6 = renderLockedResourcePrompt();
    $[10] = renderLockedResourcePrompt;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== card.isRandom) {
    t7 = card.isRandom && <Badge className={styles["random-item-indicator"]}>Random</Badge>;
    $[12] = card.isRandom;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = classnames(styles["learn-content-carousel-card__action"], "card-with-hover-effect");
    t9 = classnames(styles["learn-content-carousel-card"]);
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  const t10 = card?.ThumbnailImage?.ImageReference?.Url ?? "";
  let t11;
  if ($[16] !== cmsContent.callToSignInLabel || $[17] !== cmsContent.number || $[18] !== fallbackImageSource || $[19] !== imageAlt || $[20] !== isAuthenticated || $[21] !== isLocked || $[22] !== svgNumber || $[23] !== t10) {
    t11 = <LearnContentCarouselCardImage alt={imageAlt} callToActionText={cmsContent.callToSignInLabel} fallbackSrc={fallbackImageSource} isAuthenticated={isAuthenticated} isLocked={isLocked} numberTranslation={cmsContent.number} src={t10} svgNumber={svgNumber} />;
    $[16] = cmsContent.callToSignInLabel;
    $[17] = cmsContent.number;
    $[18] = fallbackImageSource;
    $[19] = imageAlt;
    $[20] = isAuthenticated;
    $[21] = isLocked;
    $[22] = svgNumber;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== cmsContent.membersOnlyLabel || $[26] !== isLocked) {
    t12 = isLocked && <Badge variant={BadgeVariant.Tertiary}><LockIcon className={styles["learn-content-carousel-card__lock-icon"]} /><span className={styles["learn-content-carousel-card__type-badge-text"]}>{cmsContent.membersOnlyLabel}</span></Badge>;
    $[25] = cmsContent.membersOnlyLabel;
    $[26] = isLocked;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== card.Heading) {
    t13 = card?.Heading && <h3 className={styles["learn-content-carousel-card__title"]} title={card.Heading}>{card.Heading}</h3>;
    $[28] = card.Heading;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== otherAuthorsCount) {
    t14 = otherAuthorsCount && <span className={styles["learn-content-carousel-card__author-count"]}>+{otherAuthorsCount}</span>;
    $[30] = otherAuthorsCount;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const T0 = RenderHTML;
  const t15 = styles["learn-content-carousel-card__description"];
  const t16 = getTextFromHtmlString(description);
  let t17;
  if ($[32] !== T0 || $[33] !== description || $[34] !== t15 || $[35] !== t16) {
    t17 = <T0 className={t15} html={description} title={t16} />;
    $[32] = T0;
    $[33] = description;
    $[34] = t15;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  return <>{t6}{t7}<CardLink actionClassName={t8} className={t9} data-ga-carousel-filter={selectedFilterValue} data-ga-carousel-header={carouselTitle} data-ga-exclude-tracking={true} ImageComponent={t11} onClick={() => {
      onResourceClick();
      if (isLocked || card.isRandom) {
        sendLockedResourceIntoGTM();
      }
      if (carouselTitle === "On-Demand Courses") {
        sendOnDemandCourseIntoGTM();
      }
    }} {...linkProps}><div className={styles["learn-content-carousel-card__type-badge-container"]}><Badge><ResourceTypeIcon className={styles["learn-content-carousel-card__type-icon"]} contentType={card?.ContentTypeSelection} isExternal={isExternal} /></Badge>{t12}</div><div className={styles["learn-content-carousel-card__content"]}>{t13}<div className={styles["learn-content-carousel-card__info"]}><p className={styles["learn-content-carousel-card__authors"]} title={people.join("; ")}><span className={styles["learn-content-carousel-card__first-author"]}>{firstAuthor}</span>{t14}</p>{formattedReadTimeLabel && <span className={classnames(styles["learn-content-carousel-card__read-time"], {
            [styles["learn-content-carousel-card__read-time--with-separator"]]: firstAuthor
          })} title={formattedReadTimeLabel}>{formattedReadTimeLabel}</span>}</div>{t17}</div></CardLink></>;
};